import { useState, useEffect, useCallback } from 'react';
import { api } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import {
  CreditCard, RefreshCw, CheckCircle2, AlertTriangle, Store, Receipt,
  CalendarClock, Info, ArrowUpRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const peso = (n) => `\u20b1${(Number(n) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};
const daysUntil = (d) => {
  if (!d) return null;
  const ms = new Date(d).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
};

const STATUS_STYLES = {
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  trial: { label: 'Trial', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  trialing: { label: 'Trial', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  grace: { label: 'Grace Period', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  past_due: { label: 'Past Due', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  expired: { label: 'Expired', cls: 'bg-red-100 text-red-700 border-red-200' },
  suspended: { label: 'Suspended', cls: 'bg-red-100 text-red-700 border-red-200' },
  canceled: { label: 'Canceled', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const INVOICE_STATUS = {
  paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700 border-red-200' },
  expired: { label: 'Expired', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  canceled: { label: 'Canceled', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export default function SubscriptionSettings({ branches = [] }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [biz, setBiz] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, inv, pl, b] = await Promise.all([
        api.get('/billing/subscription').catch(() => ({ data: null })),
        api.get('/billing/invoices').catch(() => ({ data: { invoices: [] } })),
        api.get('/billing/plans').catch(() => ({ data: { plans: [] } })),
        api.get('/settings/business-info').catch(() => ({ data: {} })),
      ]);
      setSub(s.data);
      setInvoices(inv.data?.invoices || inv.data || []);
      setPlans(pl.data?.plans || pl.data || []);
      setBiz(b.data || {});
    } catch { toast.error('Failed to load subscription'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="py-16 text-center"><RefreshCw size={20} className="animate-spin mx-auto text-slate-400" /></div>;
  }
  if (!sub) {
    return <Card><CardContent className="py-12 text-center text-slate-400 text-sm">No subscription information available.</CardContent></Card>;
  }

  const status = sub.subscription_status || sub.status || 'active';
  const st = STATUS_STYLES[status] || STATUS_STYLES.active;
  const planName = sub.plan_display_name || sub.plan_name || (sub.plan || '').toString().toUpperCase();
  const validUntil = sub.valid_until || sub.subscription_valid_until;
  const dleft = daysUntil(validUntil);
  const currentPlan = plans.find(p => p.id === sub.billing_plan_id || p.plan_slug === sub.plan);
  // unlimited_branches: true when max_branches=0 (founders/enterprise override) or plan="founders"
  const isUnlimitedBranches = sub.unlimited_branches === true || sub.plan === 'founders' || sub.max_branches === 0;
  const branchesIncluded = isUnlimitedBranches ? Infinity : (currentPlan?.branches_included ?? 1);
  const extraBranches = sub.extra_branches || 0;
  const allowance = isUnlimitedBranches ? Infinity : (branchesIncluded + extraBranches);
  const pending = sub.pending_invoice;

  // Per-branch coverage: covered branches are the first `allowance` by seniority
  // (main/HQ branch first, then oldest-created) so the assignment is predictable.
  // Unlimited plans cover ALL branches.
  const sortedBranches = [...(branches || [])].sort((a, b) => {
    const am = (a.is_main || a.is_headquarters || a.is_hq) ? 0 : 1;
    const bm = (b.is_main || b.is_headquarters || b.is_hq) ? 0 : 1;
    if (am !== bm) return am - bm;
    return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
  });
  const branchRows = sortedBranches.map((b, idx) => ({
    ...b,
    covered: isUnlimitedBranches || idx < allowance,
  }));
  const coveredCount = isUnlimitedBranches ? branchRows.length : Math.min(branchRows.length, allowance);

  const printReceipt = (invoice) => {
    const ppNo = `PP-SUB-${String(invoice.id || '').slice(-8).toUpperCase()}`;
    const win = window.open('', '_blank', 'width=420,height=640');
    if (!win) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${ppNo}</title>
      <style>
        *{font-family:'Courier New',monospace;color:#111}
        body{width:300px;margin:0 auto;padding:16px}
        .c{text-align:center}.b{font-weight:700}
        .row{display:flex;justify-content:space-between;font-size:12px;margin:3px 0}
        .hr{border-top:1px dashed #000;margin:8px 0}
        .sm{font-size:10px;color:#555}
        .grand{font-size:15px;font-weight:800}
      </style></head><body>
      <div class="c b" style="font-size:15px">${(biz.business_name || 'AgriBooks').toUpperCase()}</div>
      <div class="c sm">Software Subscription</div>
      <div class="hr"></div>
      <div class="c b" style="font-size:13px">PROOF OF PAYMENT</div>
      <div class="c sm">Supplementary — NOT a BIR document.<br/>Not valid for claim of input tax.</div>
      <div class="hr"></div>
      <div class="row"><span>Ref No.</span><span class="b">${ppNo}</span></div>
      <div class="row"><span>Date Paid</span><span>${fmtDate(invoice.paid_at || invoice.created_at)}</span></div>
      <div class="row"><span>Plan</span><span>${invoice.plan_display_name || planName}</span></div>
      <div class="row"><span>Billing</span><span>${invoice.interval || ''}</span></div>
      ${invoice.payment_intent_id ? `<div class="row"><span>Txn</span><span class="sm">${invoice.payment_intent_id}</span></div>` : ''}
      <div class="hr"></div>
      <div class="row grand"><span>AMOUNT PAID</span><span>${peso(invoice.amount_php)}</span></div>
      <div class="hr"></div>
      <div class="c sm">Paid via PayMongo QR Ph<br/>Thank you for your subscription.</div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`);
    win.document.close();
  };

  return (
    <div className="space-y-5" data-testid="subscription-settings">
      {/* Pending payment banner */}
      {pending && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="sub-pending-banner">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Payment pending — {peso(pending.amount_php)}</p>
                <p className="text-xs text-amber-700 mt-0.5">{pending.plan_display_name || 'Subscription'} · scan the PayMongo QR to complete and keep your service active.</p>
              </div>
            </div>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 shrink-0" data-testid="sub-pay-now-btn" onClick={() => navigate('/upgrade')}>
              Pay Now <ArrowUpRight size={14} className="ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Current plan */}
      <Card style={{ border: '1px solid var(--border-default)' }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <CreditCard size={18} className="text-[#1A4D2E]" /> Current Plan
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-2xl font-bold text-[#1A4D2E]" data-testid="sub-plan-name" style={{ fontFamily: 'Manrope' }}>{planName}</span>
            <Badge className={`${st.cls} border`} data-testid="sub-status-badge">{st.label}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Branches included</p>
              <p className="text-sm font-bold text-slate-800">
                {isUnlimitedBranches ? 'Unlimited' : `${branchesIncluded}${extraBranches > 0 ? ` + ${extraBranches} extra` : ''}`}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Branches in use</p>
              <p className="text-sm font-bold text-slate-800">{branchRows.length}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 col-span-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Valid until</p>
              <p className="text-sm font-bold text-slate-800">{fmtDate(validUntil)}{dleft != null && <span className={`ml-1.5 text-xs font-medium ${dleft <= 7 ? 'text-amber-600' : 'text-slate-400'}`}>({dleft >= 0 ? `${dleft} day${dleft === 1 ? '' : 's'} left` : 'expired'})</span>}</p>
            </div>
          </div>

          {/* Honest renewal: PayMongo QR is manual scan-to-pay, no auto-charge */}
          <div className="mt-4 rounded-xl border border-[#1A4D2E]/20 bg-[#1A4D2E]/[0.04] p-4">
            <div className="flex items-start gap-2.5">
              <CalendarClock size={18} className="text-[#1A4D2E] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#1A4D2E]">Renewal</p>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Payments use <strong>PayMongo QR Ph</strong> (scan-to-pay) — there's <strong>no automatic card charge</strong>. We email you a reminder before {fmtDate(validUntil)}; renew by scanning a fresh QR to avoid interruption.
                </p>
                <Button size="sm" className="mt-2.5 bg-[#1A4D2E] hover:bg-[#143d24] h-8 text-xs" data-testid="sub-renew-btn" onClick={() => navigate('/upgrade')}>
                  <RefreshCw size={13} className="mr-1.5" /> Renew / Change Plan
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Per-branch status */}
      <Card style={{ border: '1px solid var(--border-default)' }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Store size={18} className="text-[#1A4D2E]" /> Per-Branch Status
          </CardTitle>
          <p className="text-sm text-slate-500">
            {isUnlimitedBranches
              ? `All ${branchRows.length} branch${branchRows.length === 1 ? '' : 'es'} covered — unlimited plan.`
              : `${coveredCount} of ${branchRows.length} branch${branchRows.length === 1 ? '' : 'es'} covered by your plan (${allowance} allowed).`}
          </p>
        </CardHeader>
        <CardContent>
          {!isUnlimitedBranches && branchRows.length > allowance && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 flex items-center gap-2" data-testid="sub-overlimit-warning">
              <Info size={14} className="text-amber-600" />
              <p className="text-xs text-amber-700">{branchRows.length - allowance} branch(es) exceed your plan allowance. Add extra branches or upgrade to keep them active on renewal.</p>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Branch</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead className="text-right">Coverage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {branchRows.map((b) => (
                  <TableRow key={b.id} data-testid={`sub-branch-${b.id}`}>
                    <TableCell className="text-sm font-medium">{b.name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{b.region || '—'}</TableCell>
                    <TableCell className="text-right">
                      {b.covered
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-[10px]"><CheckCircle2 size={11} className="mr-1" />Covered</Badge>
                        : <Badge className="bg-amber-100 text-amber-700 border-amber-200 border text-[10px]">Over limit</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Invoice history */}
      <Card style={{ border: '1px solid var(--border-default)' }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Receipt size={18} className="text-[#1A4D2E]" /> Billing History
          </CardTitle>
          <p className="text-sm text-slate-500">Your subscription payments. Tap a paid invoice to print a Proof of Payment.</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Receipt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400 text-sm">No billing history yet.</TableCell></TableRow>
                )}
                {invoices.map((inv) => {
                  const istat = INVOICE_STATUS[inv.status] || INVOICE_STATUS.pending;
                  return (
                    <TableRow key={inv.id} data-testid={`sub-invoice-${inv.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(inv.paid_at || inv.created_at)}</TableCell>
                      <TableCell className="text-sm">{inv.plan_display_name || planName}</TableCell>
                      <TableCell className="text-xs text-slate-500">{inv.interval || '—'}</TableCell>
                      <TableCell className="text-right text-sm font-mono">{peso(inv.amount_php)}</TableCell>
                      <TableCell><Badge className={`${istat.cls} border text-[10px]`}>{istat.label}</Badge></TableCell>
                      <TableCell className="text-right">
                        {inv.status === 'paid' ? (
                          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`sub-receipt-btn-${inv.id}`} onClick={() => printReceipt(inv)}>
                            <Receipt size={12} className="mr-1" /> Print
                          </Button>
                        ) : <span className="text-xs text-slate-300">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
